import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import env from "@/utils/env";
import { ACCESS_TOKEN_STORAGE_KEY } from "@/const";
import type {
  OpportunityGeneratorFilter,
  OpportunityGeneratorScoringProfileSalesplaySearch,
  ModelScoreInput,
  ModelScoreFilterInput,
} from "@/generated/company-search";

// Dedicated instance: skips the shared 401 interceptor so an emback auth failure can't log the user out of Market Analyzer.
export const embackAxios: AxiosInstance = axios.create({
  baseURL: env.EMBACK_URL,
  headers: { "Content-Type": "application/json" },
});

embackAxios.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    try {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
      if (!accessToken) {
        return config;
      }

      const newConfig = { ...config };
      newConfig.headers.Authorization = `Bearer ${accessToken}`;

      return newConfig;
    } catch {
      return config;
    }
  }
);

export interface CreateWorkbookFromSegmentBody {
  name: string;
  filters: OpportunityGeneratorFilter;
  scoringProfiles?: OpportunityGeneratorScoringProfileSalesplaySearch[];
  localizeInstalls?: boolean;
  // V2 (argo model) scoring. Every active model in `modelScores` is kept on the
  // list, so Sales Copilot shows its scores. Those `modelScoreFilter` targets
  // are forwarded to opportunityGeneratorExportIds so a segment filtered by
  // model score saves the same set it shows on screen; `tenantId` resolves them.
  modelScores?: ModelScoreInput[];
  modelScoreFilter?: ModelScoreFilterInput[];
  tenantId?: string;
  /** The offering the list opens on in Sales Copilot, chosen by an admin. */
  offeringId?: string;
}

export interface CreatedWorkbook {
  id: string;
  name: string;
  segmentSnapshot?: { rowCount: number } | null;
}

export async function createWorkbookFromSegment(
  body: CreateWorkbookFromSegmentBody
): Promise<CreatedWorkbook> {
  const { data } = await embackAxios.post<CreatedWorkbook>(
    "/workbooks/from-segment",
    body
  );
  return data;
}
